import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { listPadEntries } from "../TenantForm/Helpers/PadInfo/api";
import PadHistory from "../TenantForm/Helpers/PadInfo/PadHistory";
import { categoryMeta, money } from "../TenantForm/Helpers/PadInfo/padInfoTypes";
import { resolveViewer } from "../../../../components/NotificationCenter/useNotificationFeed";

// The full PAD editor drags in the document previewer (react-pdf); the report only
// needs it once someone actually presses Edit, so it stays out of this bundle path.
const PadInfoModal = lazy(() => import("../TenantForm/Helpers/PadInfo/PadInfoModal"));

const C = { border: "#E4E9F2", muted: "#64748B", text: "#0F1F35", soft: "#F7F9FC" };

/** One labelled fact in the details grid; renders nothing when there is no value. */
const Fact = ({ label, children }) => {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
};

/**
 * "View Details" for one PAD report row: the entry's recorded facts, its attachments,
 * and the full edit history (who changed which fields, old → new) — with an Edit button
 * for people the server lets write (company/admin, and managers/auditors with the
 * mastersheet privilege). The server enforces the same rule again on save, so this
 * gating is a courtesy, not the security boundary.
 */
const PadDetailModal = ({ isOpen, toggle, row, masterSheetData, propertyName, onChanged }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);

  // The report row knows its unit; the mastersheet (already in memory) knows the item —
  // the same resolution the deep link from the notification center uses.
  const item = useMemo(() => {
    const want = String(row?.unit_no ?? "").trim().toLowerCase();
    if (!want) return null;
    return (masterSheetData?.items || []).find(
      (it) => String(it.unitNo ?? it.unit_no ?? "").trim().toLowerCase() === want
    ) || null;
  }, [masterSheetData, row?.unit_no]);

  const loadEntry = () => {
    if (!row?.pad_id) return;
    if (!item?._id) {
      setError("This unit is no longer on the live mastersheet, so the PAD can only be seen from the Archived Mastersheet.");
      return;
    }
    setLoading(true);
    setError("");
    listPadEntries(item._id, true)
      .then(({ data }) => {
        const rows = data?.data || [];
        const found = rows.find((e) => e.pad_id === row.pad_id);
        if (!found) throw new Error("PAD entry not found for this row.");
        setEntry(found);
      })
      .catch((err) => setError(err?.response?.data?.message || err.message || "Failed to load the PAD entry."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) {
      setEntry(null);
      setError("");
      setEditorOpen(false);
      return;
    }
    loadEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, row?.pad_id, item?._id]);

  const viewer = useMemo(() => resolveViewer(), []);
  const canEdit = !!viewer && !!entry && !entry.tenancy_archived && !entry.voided && !!item;

  const meta = entry ? categoryMeta(entry.category) : null;
  const verified = !!entry?.verification?.verified;

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
        <ModalHeader toggle={toggle}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {meta ? (
              <span style={{ background: meta.color, color: "#fff", borderRadius: 7, padding: "2px 10px", fontSize: 12.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <FeatherIcon icon={meta.icon} size={12} /> {meta.label}
              </span>
            ) : null}
            Unit {row?.unit_no} · PAD Details
          </span>
        </ModalHeader>
        <ModalBody style={{ background: "#fff" }}>
          {loading ? (
            <div className="text-center p-4"><Spinner color="primary" size="sm" /> Loading PAD entry…</div>
          ) : error ? (
            <p className="text-danger mb-0">{error}</p>
          ) : !entry ? (
            <p className="text-muted mb-0">No entry loaded.</p>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: "12px 16px", padding: "4px 0 12px" }}>
                <Fact label="PAD ID">{entry.pad_id}</Fact>
                <Fact label="Resident">{entry.tenant_names || row?.tenant || "—"}</Fact>
                <Fact label="Effective date">{entry.effective_date || "—"}</Fact>
                <Fact label="Total rent">{row?.rent != null ? money(row.rent) : entry.derived?.rent != null ? money(entry.derived.rent) : null}</Fact>
                <Fact label="Pull day">{entry.pull_day || null}</Fact>
                <Fact label="Recorded by">{entry.created_by?.name || null}</Fact>
              </div>

              {entry.derived?.details ? (
                <div style={{ background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 12.5, color: C.text, marginBottom: 12 }}>
                  {entry.derived.details}
                </div>
              ) : null}

              {entry.notes ? (
                <div style={{ marginBottom: 12 }}>
                  <Fact label="Notes"><span style={{ whiteSpace: "pre-wrap" }}>{entry.notes}</span></Fact>
                </div>
              ) : null}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 12.5, fontWeight: 600 }}>
                {verified ? (
                  <span style={{ color: "#0F6E56", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <FeatherIcon icon="check-circle" size={13} /> Verified by accounting{entry.verification?.verified_by_name ? ` — ${entry.verification.verified_by_name}` : ""}
                  </span>
                ) : (
                  <span style={{ color: "#B45309", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <FeatherIcon icon="alert-circle" size={13} /> Not yet verified by accounting
                  </span>
                )}
                {entry.tenancy_archived ? (
                  <span style={{ color: C.muted, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <FeatherIcon icon="archive" size={13} /> Archived tenancy — view only
                  </span>
                ) : null}
              </div>

              {(row?.attachments || []).length > 0 ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 5 }}>Attachments</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.attachments.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" title={a.filename}
                        style={{ fontSize: 12, color: "#1D4ED8", background: "#EFF6FF", border: "1px solid #DBEAFE", borderRadius: 999, padding: "3px 11px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <FeatherIcon icon="paperclip" size={11} /> {a.filename}
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <FeatherIcon icon="clock" size={13} /> Edit history
                </div>
                <PadHistory audit={entry.audit_trail} />
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
          {canEdit ? (
            <Button color="primary" size="sm" onClick={() => setEditorOpen(true)}>
              <FeatherIcon icon="edit-3" size={13} className="me-1" /> Edit PAD
            </Button>
          ) : null}
          <Button color="light" size="sm" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>

      {editorOpen && entry && item ? (
        <Suspense fallback={null}>
        <PadInfoModal
          isOpen={editorOpen}
          toggle={() => setEditorOpen(false)}
          itemId={item._id}
          item={item}
          unitNo={String(row?.unit_no ?? "")}
          buildingName={masterSheetData?.building_name || ""}
          propertyName={propertyName || ""}
          tenantNames={entry.tenant_names || row?.tenant || ""}
          existing={entry}
          onSaved={() => {
            setEditorOpen(false);
            loadEntry();
            if (onChanged) onChanged();
          }}
        />
        </Suspense>
      ) : null}
    </>
  );
};

export default PadDetailModal;
